import { BUILDS_COLLECTION, MANAGED_BUILDS_COLLECTION } from '@/constants/constants';
import {
  GlobalBlockTypes,
  ManagedBuildReference,
  PaymentSettings,
  TBuild,
  TypeOfBuild,
} from '@/types';
import { getCollection } from '@/utils/helpers/getCollectionByName';
import { realmApp } from '@/utils/realmConfig';
import { BSON } from 'realm-web';
import { getAttachments, uploadImage } from '../Attachments/attachments.service';
import { findLatestBuildVersion } from '@/pages/checkoutFlows/helpers';

export interface BuildSettings {
  name?: string;
  allowCashManagement?: boolean;
  requirePin?: boolean;
  previewImage?: string;
  description?: string;
  tag?: string;
  seeDetailsURL?: string;
}

export async function findOneBuildByCompany(companyId: string) {
  try {
    const buildsCollection = getCollection(BUILDS_COLLECTION);
    const build = await buildsCollection?.findOne({
      companyId: new BSON.ObjectID(companyId),
    });
    return BSON.EJSON.deserialize(build) as TBuild;
  } catch (error) {
    console.error('Error fetching build', error);
  }
}

export async function fetchBuildsByCompany({
  companyId,
  buildType,
  isPublished,
  globalBlockType,
}: {
  companyId: string;
  buildType?: TypeOfBuild;
  isPublished?: boolean;
  globalBlockType?: GlobalBlockTypes;
}): Promise<TBuild[] | undefined> {
  try {
    const buildsCollection = getCollection(BUILDS_COLLECTION);
    const builds = await buildsCollection?.aggregate([
      {
        $match: {
          companyId: new BSON.ObjectID(companyId),
          buildType: buildType || { $exists: true },
          ...(isPublished !== undefined && { isPublished }),
          ...(globalBlockType ? { globalBlockType } : {}),
        },
      },
      {
        $project: {
          _id: 1,
          name: 1,
          version: 1,
          accountId: 1,
          companyId: 1,
          buildType: 1,
          description: 1,
          isPublished: 1,
          previewImage: 1,
          sourceId: 1,
          size: 1,
          seeDetailsURL: 1,
          tag: 1,
        },
      },
    ]);

    return BSON.EJSON.deserialize(builds) as TBuild[];
  } catch (error) {
    console.error('Error fetching builds', error);
  }
}

export async function fetchManagedReferencesByCompany(
  companyId: string,
  gbType?: GlobalBlockTypes,
) {
  const MBCol = getCollection(MANAGED_BUILDS_COLLECTION);
  const managedBuilds = await MBCol?.aggregate([
    {
      $match: {
        companyId: new BSON.ObjectId(companyId),
      },
    },
    {
      $lookup: {
        from: BUILDS_COLLECTION,
        localField: 'buildId',
        foreignField: '_id',
        as: 'build',
      },
    },
    {
      $unwind: '$build',
    },
    {
      $match: {
        'build.buildType': gbType ? TypeOfBuild.OLIVER_GLOBAL_BLOCK : TypeOfBuild.OLIVER_THEME,
        ...(gbType ? { 'build.globalBlockType': gbType } : {}),
      },
    },
    {
      $project: {
        build: 0,
      },
    },
  ]);

  if (!managedBuilds) {
    return [];
  } else {
    return BSON.EJSON.deserialize(managedBuilds) as ManagedBuildReference[];
  }
}

export async function fetchManagedBuildsByCompany({
  companyId,
  gbType,
}: {
  companyId: string;
  gbType?: GlobalBlockTypes;
}): Promise<TBuild[] | undefined> {
  const managedBuildReferences = await fetchManagedReferencesByCompany(companyId, gbType);
  const libraryBuilds = await fetchPublicManagedBuilds({ gbType });
  const unpublishedBuilds = libraryBuilds.filter((b) =>
    managedBuildReferences.some((ref) => ref.buildId === b._id),
  );
  unpublishedBuilds.forEach((build) => {
    build.version = findLatestBuildVersion(build._id, libraryBuilds);
  });
  return BSON.EJSON.deserialize(unpublishedBuilds) as TBuild[];
}

export async function fetchPublicManagedBuilds(config?: { gbType?: GlobalBlockTypes }) {
  const buildsCollection = getCollection(BUILDS_COLLECTION);
  const filtered = await buildsCollection?.aggregate([
    {
      $match: {
        buildType: config?.gbType ? TypeOfBuild.OLIVER_GLOBAL_BLOCK : TypeOfBuild.OLIVER_THEME,
        ...(config?.gbType ? { globalBlockType: config.gbType } : {}),
      },
    },
  ]);
  return BSON.EJSON.deserialize(filtered) as TBuild[];
}

export async function insertOneBuild(newBuild: {
  name: string;
  companyId: string;
  accountId: string;
  gbType?: GlobalBlockTypes;
  size?: {
    sizeKey: string;
    width: number;
    height: number;
  };
}) {
  const { name, companyId, accountId, gbType, size } = newBuild;
  const response = await realmApp.currentUser?.functions.createBuild({
    name,
    accountId,
    companyId,
    size,
    ...(gbType ? { buildType: 'globalBlock' } : {}),
    ...(gbType ? { globalBlockType: gbType } : {}),
  });
  return response;
}

export async function deleteBuild(buildId: string) {
  const response = await realmApp.currentUser?.functions.deleteBuild(buildId);
  return response;
}
export async function uninstallManagedBuild(details: { buildId: string; companyId: string }) {
  const response = await realmApp.currentUser?.functions.uninstallManagedBuild(details);
  return response;
}

export async function duplicateBuild(buildId: string) {
  const response = await realmApp.currentUser?.functions.duplicateBuild(buildId);
  return response;
}

export async function updateBuildSettings(buildId: string, updates: BuildSettings) {
  const response = await realmApp.currentUser?.functions.updateBuildSettings(buildId, updates);

  return response;
}

export async function installManagedBuild(details: {
  buildId: string;
  companyId: string;
  enableCashManagement: boolean;
  requireEmployeePin: boolean;
  paymentSettings: PaymentSettings;
}) {
  const response = await realmApp.currentUser?.functions.installManagedBuild(details);
  return response;
}

export async function updateInstalledManagedBuild(query: {
  managedId: string;
  settings: {
    enableCashManagement: boolean;
    requireEmployeePin: boolean;
    paymentSettings: PaymentSettings;
  };
}) {
  const response = await realmApp.currentUser?.functions.updateInstalledManagedBuild(query);
  return response;
}

// export const fetchTheBuilds = async (companyID: string) => {
//   const customBuildsPromise = fetchBuildsByCompany({
//     companyId: companyID,
//     buildType: TypeOfBuild.BUILD
//   }) as Promise<TBuild[]>;
//   const managedBuildsPromise = fetchManagedBuildsByCompany({
//     companyId: companyID
//   }) as Promise<TBuild[]>;

//   const [customBuilds, managedBuilds] = await Promise.all([
//     customBuildsPromise,
//     managedBuildsPromise
//   ]);
//   const unpublishedCustomBuilds = customBuilds.filter((b) => !b.isPublished);
//   unpublishedCustomBuilds.forEach((build) => {
//     build.version = findLatestBuildVersion(build._id, customBuilds);
//   });

//   return [...unpublishedCustomBuilds, ...managedBuilds];
// };

export const fetchTheBuilds = async (companyID: string, gbType?: GlobalBlockTypes) => {
  const customBuildsPromise = fetchBuildsByCompany({
    companyId: companyID,
    buildType: gbType ? TypeOfBuild.GLOBAL_BLOCK : TypeOfBuild.BUILD,
    isPublished: undefined,
    globalBlockType: gbType,
  }) as Promise<TBuild[]>;
  const managedBuildsPromise = fetchManagedBuildsByCompany({
    companyId: companyID,
    gbType: gbType,
  }) as Promise<TBuild[]>;

  const [customBuilds, managedBuilds] = await Promise.all([
    customBuildsPromise,
    managedBuildsPromise,
  ]);
  const unpublishedCustomBuilds = customBuilds.filter((b) => !b.isPublished);
  unpublishedCustomBuilds.forEach((build) => {
    build.version = findLatestBuildVersion(build._id, customBuilds);
  });

  return [...unpublishedCustomBuilds, ...managedBuilds];
};

export const fetchManagedBuilds = async (config?: { gbType?: GlobalBlockTypes }) => {
  const libraryBuilds = await fetchPublicManagedBuilds(config);
  const buildsAllVersions = [...(libraryBuilds || [])];
  const notPublishedOnly = buildsAllVersions.filter((b) => !b.isPublished);
  notPublishedOnly.forEach((build) => {
    build.version = findLatestBuildVersion(build._id, buildsAllVersions);
  });
  return notPublishedOnly.filter((b) => b.version);
};

export async function fetchThemesBuilds({
  currentUserType,
  currentUserAccountId,
  tag,
}: {
  currentUserType: string;
  currentUserAccountId?: string;
  tag?: string[];
}): Promise<TBuild[] | undefined> {
  try {
    const buildsCollection = getCollection(BUILDS_COLLECTION);

    let filters: any;

    if (currentUserType === 'RESELLER') {
      filters = {
        isPublished: false,
        $or: [
          {
            buildType: TypeOfBuild.ORG_THEME,
            accountId: new BSON.ObjectID(currentUserAccountId),
            ...(tag ? { tag: { $in: tag } } : {}),
          },
          {
            buildType: TypeOfBuild.OLIVER_THEME,
            ...(tag ? { tag: { $in: tag } } : {}),
          },
        ],
      };
    } else {
      filters = {
        isPublished: false,
        buildType: TypeOfBuild.OLIVER_THEME,
        ...(tag ? { tag: { $in: tag } } : {}),
      };
    }

    const builds = await buildsCollection?.aggregate([
      {
        $match: filters,
      },
      {
        $project: {
          _id: 1,
          name: 1,
          version: 1,
          accountId: 1,
          companyId: 1,
          buildType: 1,
          description: 1,
          isPublished: 1,
          previewImage: 1,
          sourceId: 1,
          tag: 1,
          size: 1,
          seeDetailsURL: 1,
        },
      },
    ]);

    return BSON.EJSON.deserialize(builds) as TBuild[];
  } catch (error) {
    console.error('Error fetching builds', error);
  }
}

export const extractImageURLSFromPagesString = (input?: string): string[] => {
  if (!input) {
    return [];
  }
  const regex =
    /https:\/\/storage\.googleapis\.com\/[^"']*?\.(jpg|jpeg|png|gif|bmp|tiff|webp|svg)/gi;
  const matches = input.match(regex) || [];
  const uniques = Array.from(new Set(matches));
  return uniques;
};

/**
 * returns a new string with all images from the base layer string copied to the new account
 * @param baselayerString
 * @param companyId
 * @param accountId
 * @returns
 */
export const copyMediaFromBaseLayerString = async (
  baselayerString: string,
  companyId: string,
  pathPrefix?: string,
): Promise<string> => {
  const allImageUrls = extractImageURLSFromPagesString(baselayerString);
  const urlMap = new Map<string, string>();

  const existingAttachements = (await getAttachments(companyId)) || [];
  const fetchAndUploadImage = async (imageUrl: string): Promise<void> => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${imageUrl}`);
      }
      const blob = await response.blob();

      const fileName = imageUrl.split('/').pop() || 'image.jpg';
      const uniqueName = pathPrefix ? `${pathPrefix}-${fileName}` : new Date().getTime() + fileName;

      if (existingAttachements.map((a: any) => a.name).includes(uniqueName)) {
        return;
      }
      const file = new File([blob], uniqueName, { type: blob.type });

      const newImageUrl = (await uploadImage(file, companyId)) || imageUrl;
      urlMap.set(imageUrl, newImageUrl);
    } catch (error) {
      console.error(error);
    }
  };

  await Promise.all(allImageUrls.map(fetchAndUploadImage));

  const replaceUrlsInString = (input: string): string => {
    let output = input;
    urlMap.forEach((newUrl, oldUrl) => {
      try {
        const regex = new RegExp(oldUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        output = output.replace(regex, newUrl);
      } catch {
        console.warn('Failed to replace url', oldUrl);
      }
    });
    return output;
  };
  const newBaseLayerString = replaceUrlsInString(baselayerString);
  return newBaseLayerString;
};

export const cloneManagedBuild = async (build: TBuild, companyId: string, accountId: string) => {
  // cloning global blocks
  const globalBlockstoClone = build.globalBlocks;
  const parsedGBs = JSON.parse(globalBlockstoClone || '{}');
  const keys = Object.keys(parsedGBs);
  const buildCol = getCollection(BUILDS_COLLECTION);
  const blocks = await buildCol?.find({
    _id: { $in: keys.map((id) => new BSON.ObjectID(id)) },
  });
  const newBlocksData = {} as Record<string, string>;
  //saving the new block ids to update the components
  const oldToNewBlockIds = {} as Record<string, string>;

  if (build.globalBlocks && blocks) {
    for (const block of blocks) {
      const newBlockSaved = {
        ...block,
        _id: new BSON.ObjectID(),
        companyId: new BSON.ObjectID(companyId),
        accountId: new BSON.ObjectID(accountId),
        name: `${block.name} - Clone`,
        isPublished: false,
      };
      const newBlock = {
        ...block,
        _id: new BSON.ObjectID(),
        companyId: new BSON.ObjectID(companyId),
        accountId: new BSON.ObjectID(accountId),
        name: `${block.name} - Clone`,
        sourceId: newBlockSaved._id,
      };
      //adding both the saved (draft) and a published version of the block
      await buildCol?.insertOne(newBlockSaved);
      await buildCol?.insertOne(newBlock);
      newBlocksData[newBlock._id.toHexString()] = {
        ...parsedGBs[block._id.toHexString()],
        name: newBlock.name,
      };
      oldToNewBlockIds[block._id.toHexString()] = newBlock._id.toHexString();
    }
  }

  //this is to replace the old block ids with the new ones in the components
  const processContent = (content: string) => {
    if (!content) return content;
    keys.forEach((oldId) => {
      const newId = oldToNewBlockIds[oldId];
      const regex = new RegExp(`"(${oldId})"`, 'g');
      content = content.replace(regex, `"${newId}"`);
    });
    return content;
  };

  const updatedPages = processContent(build.pages);
  const updatedPopups = processContent(build.popups);
  const updatedCfds = build.cfds ? processContent(build.cfds) : '';

  //this is to copy all the images to the users account
  const copyPages = copyMediaFromBaseLayerString(updatedPages, companyId, build.name);
  const copyPopups = copyMediaFromBaseLayerString(updatedPopups, companyId, build.name);
  const copyCfds = build.cfds
    ? copyMediaFromBaseLayerString(updatedCfds, companyId, build.name)
    : '';

  const copyPreviewImage = build.previewImage
    ? await copyMediaFromBaseLayerString(processContent(build.previewImage), companyId, build.name)
    : '';

  const [resolvedPages, resolvedPopups, resolvedCFDs] = await Promise.all([
    copyPages,
    copyPopups,
    copyCfds,
  ]);

  const newBuild = {
    ...build,
    _id: new BSON.ObjectID(),
    companyId: new BSON.ObjectID(companyId),
    accountId: new BSON.ObjectID(accountId),
    previewImage: copyPreviewImage,
    name: `${build.name} - Clone`,
    buildType: TypeOfBuild.BUILD,
    pages: resolvedPages,
    popups: resolvedPopups,
    cfds: resolvedCFDs,
    isPublished: false,
    globalBlocks: JSON.stringify(newBlocksData),
  };

  const response = await buildCol?.insertOne(newBuild);
  return {
    status: response ? 'success' : 'error',
    message: response ? 'Build cloned successfully' : 'Failed to clone build',
  };
};
